import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import {
  ACTIVE_THREAD_STATES,
  type HubPresence,
  type ProjectEvent,
  type ThreadActivity,
} from '@hub/contracts'
import { getUserColor } from './colors'
import { ProjectHubClient, type HubStatus } from '@/lib/hub/project-hub-client'
import { useAuthState } from '@/lib/auth-state'
import { getPageKey, type NavKey } from '@/lib/navigation'

export type ConnectionStatus = HubStatus

export interface PresenceUser {
  userId: string
  name: string
  avatarUrl?: string
  color: string
  currentRoute: string
  /** Nav key of the page (translated by the viewer, not the sender). */
  currentRouteKey: NavKey | null
}

interface PresenceContextValue {
  onlineUsers: PresenceUser[]
  connectionStatus: ConnectionStatus
  followUser: (userId: string) => void
  /** Chat threads the agent is currently working in, keyed by thread id. */
  activeThreads: ReadonlyMap<string, ThreadActivity>
  /** The project socket (PLAN-081) — feature hooks subscribe to its events. */
  hub: ProjectHubClient | null
}

/**
 * An entry the chat bridge never cleared (agent crash, evicted DO) must not
 * glow forever — ignore it once it is this old. Same threshold as the chat's
 * stale-status guard (use-chat-collab).
 */
const ACTIVITY_STALE_MS = 10 * 60 * 1000
const EMPTY_ACTIVITY: ReadonlyMap<string, ThreadActivity> = new Map()

function readActiveThreads(list: ThreadActivity[]): ReadonlyMap<string, ThreadActivity> {
  const now = Date.now()
  const result = new Map<string, ThreadActivity>()
  for (const entry of list) {
    if (!ACTIVE_THREAD_STATES.has(entry.state)) continue
    if (now - Date.parse(entry.updatedAt) > ACTIVITY_STALE_MS) continue
    result.set(entry.threadId, entry)
  }
  return result
}

/**
 * Where following `userId` leads: the route of their most recent connection
 * (the hub lists connections oldest first), the same one the avatar tooltip
 * shows. An older tab — or a socket the hub has not noticed is gone — must
 * not win (board fm4q).
 */
export function followRouteFor(presence: HubPresence[], userId: string): string | null {
  for (let i = presence.length - 1; i >= 0; i--) {
    const p = presence[i]
    if (p.userId === userId && p.currentRoute) return p.currentRoute
  }
  return null
}

/** One entry per user (several tabs collapse to the most recent route). */
function toOnlineUsers(presence: HubPresence[], selfUserId: string): PresenceUser[] {
  const users = new Map<string, PresenceUser>()
  for (const p of presence) {
    if (p.userId === selfUserId) continue
    users.set(p.userId, {
      userId: p.userId,
      name: p.name,
      avatarUrl: p.avatarUrl ?? undefined,
      color: getUserColor(p.userId),
      currentRoute: p.currentRoute,
      currentRouteKey: (p.currentRouteKey as NavKey | null) ?? null,
    })
  }
  return [...users.values()]
}

const PresenceContext = createContext<PresenceContextValue | undefined>(undefined)

interface PresenceProviderProps {
  projectId: string
  children: ReactNode
}

export function PresenceProvider({ projectId, children }: PresenceProviderProps) {
  const { me } = useAuthState()
  const { pathname, searchStr } = useLocation()
  const navigate = useNavigate()
  const [hub, setHub] = useState<ProjectHubClient | null>(null)
  const [onlineUsers, setOnlineUsers] = useState<PresenceUser[]>([])
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const [activeThreads, setActiveThreads] =
    useState<ReadonlyMap<string, ThreadActivity>>(EMPTY_ACTIVITY)
  // The search string is part of where someone is: the inbox keeps the open
  // email and folder there (`?email=…`), the task board the open task.
  const route = pathname + searchStr
  const routeRef = useRef({ route, pathname })
  routeRef.current = { route, pathname }

  useEffect(() => {
    if (!me) return
    const userId = me.user.id
    const client = new ProjectHubClient(projectId)
    const offs = [
      client.on('presence', (presence) => setOnlineUsers(toOnlineUsers(presence, userId))),
      client.on('status', setConnectionStatus),
      client.on('activeThreads', (list) => setActiveThreads(readActiveThreads(list))),
      // Thread list changes made by other members: re-emit as the window
      // event the thread lists already reload on.
      client.on('event', (event: ProjectEvent) => {
        if (event.t === 'threads:changed')
          window.dispatchEvent(new CustomEvent('chat-threads-changed'))
      }),
    ]
    client.connect()
    client.setRoute(routeRef.current.route, getPageKey(routeRef.current.pathname) ?? null)
    setHub(client)
    // Re-evaluate staleness even when nothing changes on the wire.
    const staleTimer = window.setInterval(
      () => setActiveThreads(readActiveThreads(client.activeThreads)),
      60_000
    )
    return () => {
      window.clearInterval(staleTimer)
      for (const off of offs) off()
      client.close()
      setHub(null)
      setOnlineUsers([])
      setActiveThreads(EMPTY_ACTIVITY)
    }
  }, [projectId, me])

  // Tell the others where we are.
  useEffect(() => {
    hub?.setRoute(route, getPageKey(pathname) ?? null)
  }, [hub, route, pathname])

  const followUser = useCallback(
    (targetUserId: string) => {
      const target = hub ? followRouteFor(hub.presence, targetUserId) : null
      // `href`, not `to`: the route carries its search string.
      if (target) void navigate({ href: target })
    },
    [hub, navigate]
  )

  const value = useMemo<PresenceContextValue>(
    () => ({ onlineUsers, connectionStatus, followUser, activeThreads, hub }),
    [onlineUsers, connectionStatus, followUser, activeThreads, hub]
  )

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
}

const EMPTY_PRESENCE: PresenceContextValue = {
  onlineUsers: [],
  connectionStatus: 'disconnected',
  followUser: () => {},
  activeThreads: EMPTY_ACTIVITY,
  hub: null,
}

export function usePresence(): PresenceContextValue {
  return useContext(PresenceContext) ?? EMPTY_PRESENCE
}

/** The project socket, or null outside a project / before it connects. */
export function useProjectHub(): ProjectHubClient | null {
  return usePresence().hub
}

/**
 * Subscribe to project events matching `types`; `handler` is called with the
 * event. Handlers are kept in a ref so callers can pass inline closures.
 */
export function useProjectEvent(
  types: ReadonlyArray<ProjectEvent['t']>,
  handler: (event: ProjectEvent) => void
): void {
  const hub = useProjectHub()
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  const key = types.join('|')
  useEffect(() => {
    if (!hub) return
    const wanted = new Set(key.split('|'))
    return hub.on('event', (event) => {
      if (wanted.has(event.t)) handlerRef.current(event)
    })
  }, [hub, key])
}
